import { Plus, Trash2 } from "lucide-react";
import { ListInput } from "@/components/common";

export const SECTIONS = [
  ["campaign_name", "Campaign name", "str"], ["executive_summary", "Executive summary", "text"], ["objective", "Objective", "text"],
  ["audience", "Audience", "text"], ["offer", "Offer", "text"], ["positioning", "Positioning", "text"], ["core_message", "Core message", "text"],
  ["messaging_pillars", "Messaging pillars", "list"], ["channel_mix", "Recommended channel mix & role of each channel", "channels"],
  ["content_themes", "Content themes", "list"], ["phases", "Campaign phases", "phases"], ["conversion_path", "Conversion path", "path"],
  ["measurement_plan", "Measurement plan", "metrics"], ["risks", "Risks", "list"], ["assumptions", "Assumptions", "list"],
  ["required_approvals", "Required approvals", "list"],
];

const OBJ_FIELDS = {
  channels: [["channel", "Channel"], ["role", "Role", 2], ["allocation_pct", "Allocation %", 0, "number"]],
  phases: [["name", "Phase"], ["timing", "Timing"], ["goal", "Goal", 2], ["activities", "Activities (one per line)", 2, "list"]],
  metrics: [["metric", "Metric", 2], ["target", "Target"], ["cadence", "Cadence"]],
};

function ObjectListEditor({ kind, value = [], onChange, testId }) {
  const fields = OBJ_FIELDS[kind];
  const set = (i, k, v) => onChange(value.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  return (
    <div className="space-y-3" data-testid={testId}>
      {value.map((row, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 rounded-lg border border-[#172342] bg-[#050816]/60">
          {fields.map(([k, label, span, type]) => (
            <label key={k} className={span === 2 ? "sm:col-span-2" : ""}>
              <span className="cq-muted-label block mb-1">{label}</span>
              {type === "list" ? <ListInput rows={2} value={row[k] || []} onChange={(v) => set(i, k, v)} /> :
                <input type={type || "text"} className="cq-input !py-2" value={row[k] ?? ""} onChange={(e) => set(i, k, type === "number" ? Number(e.target.value) : e.target.value)} />}
            </label>
          ))}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="sm:col-span-4 justify-self-end text-xs text-[#FF7792] flex items-center gap-1"><Trash2 size={12} /> Remove</button>
        </div>
      ))}
      <button type="button" data-testid={`${testId}-add`} onClick={() => onChange([...value, {}])} className="cq-btn cq-btn-ghost !py-1.5 !text-xs"><Plus size={12} /> Add row</button>
    </div>
  );
}

export function SectionEditor({ k, kind, value, onChange }) {
  const testId = `strategy-edit-${k}`;
  if (kind === "str") return <input data-testid={testId} className="cq-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (kind === "text") return <textarea data-testid={testId} rows={4} className="cq-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (kind === "list" || kind === "path") return <ListInput testId={testId} rows={4} value={value} onChange={onChange} />;
  return <ObjectListEditor testId={testId} kind={kind} value={value || []} onChange={onChange} />;
}

export function SectionView({ kind, value }) {
  const empty = <span className="text-sm text-[#5D6B8C] italic">Not specified</span>;
  if (kind === "str" || kind === "text") return value ? <p className="text-sm text-[#D5DDEB] leading-relaxed whitespace-pre-wrap">{value}</p> : empty;
  if (!value?.length) return empty;
  if (kind === "list") return <ul className="space-y-1.5">{value.map((v, i) => <li key={i} className="text-sm text-[#D5DDEB] flex gap-2.5"><span className="text-[#22E6F2] mt-[7px] w-1 h-1 rounded-full bg-[#22E6F2] shrink-0" />{v}</li>)}</ul>;
  if (kind === "path") return (
    <div className="flex flex-wrap items-center gap-2">{value.map((v, i) => (
      <span key={i} className="flex items-center gap-2"><span className="px-3 py-1.5 rounded-lg border border-[#243153] bg-[#050816] text-xs text-[#F7FAFF]">{v}</span>{i < value.length - 1 && <span className="text-[#22E6F2]">→</span>}</span>))}
    </div>);
  if (kind === "channels") return (
    <div className="space-y-3">{value.map((c, i) => (
      <div key={i}>
        <div className="flex items-center justify-between text-sm"><span className="font-medium text-[#F7FAFF]">{c.channel}</span><span className="font-mono text-xs text-[#22E6F2]">{c.allocation_pct ?? 0}%</span></div>
        <div className="mt-1.5 h-1 rounded-full bg-[#172342]"><div className="h-full rounded-full bg-[#2962FF]" style={{ width: `${Math.min(100, c.allocation_pct || 0)}%` }} /></div>
        <p className="text-xs text-[#AAB6CC] mt-1.5">{c.role}</p>
      </div>))}
    </div>);
  if (kind === "phases") return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{value.map((p, i) => (
      <div key={i} className="p-4 rounded-lg border border-[#172342] bg-[#050816]/60">
        <div className="flex items-center justify-between"><span className="font-mono text-[10.5px] text-[#22E6F2]">PHASE {i + 1}</span><span className="text-[11px] text-[#7C8BAD]">{p.timing}</span></div>
        <div className="font-display text-[15px] text-[#F7FAFF] mt-1">{p.name}</div>
        <p className="text-xs text-[#AAB6CC] mt-1">{p.goal}</p>
        <ul className="mt-2 space-y-1">{(p.activities || []).map((a, j) => <li key={j} className="text-xs text-[#D5DDEB]">· {a}</li>)}</ul>
      </div>))}
    </div>);
  return (
    <div className="divide-y divide-[#172342]">{value.map((m, i) => (
      <div key={i} className="py-2 grid grid-cols-3 gap-3 text-sm"><span className="text-[#F7FAFF] col-span-1">{m.metric}</span><span className="font-mono text-[#22E6F2]">{m.target}</span><span className="text-[#AAB6CC]">{m.cadence}</span></div>))}
    </div>);
}
